import { useState, useEffect, useRef } from "react";
import { apiFetch, setToken } from "../../api/api";

const T = {
  gold: "#C8A46A", goldD: "#B28C4B", goldL: "#F5EDE0",
  champagne: "#E8D7BC", border: "#EFE7DA",
  bg: "#FAF7F2", bgSoft: "#F5EFE6",
  textPri: "#2B2B2B", textSec: "#777777",
  danger: "#E27C7C",
};

// ─── Particle Canvas ───────────────────────────────────────────────────────────
function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Devicepixelratio-aware sizing so particles stay crisp and correctly scaled
    let DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W = window.innerWidth;
    let H = window.innerHeight;
    let animId;

    const applySize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    applySize();

    const onResize = () => applySize();
    window.addEventListener("resize", onResize);

    // ── Particles (reduced count + smaller radius for a subtler bg) ─────────────
    const COUNT = 42;
    const particles = Array.from({ length: COUNT }, () => ({
      x:  Math.random() * W,
      y:  Math.random() * H,
      r:  Math.random() * 1.3 + 0.5,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      opacity: Math.random() * 0.35 + 0.12,
    }));

    // ── Zigzag lines (fewer, softer — accent only, not the focal point) ─────────
    const ZCOUNT = 7;
    const zigs = Array.from({ length: ZCOUNT }, () => ({
      x:       Math.random() * W,
      y:       Math.random() * H,
      angle:   Math.random() * Math.PI * 2,
      speed:   Math.random() * 0.4 + 0.15,
      opacity: Math.random() * 0.1 + 0.04,
      seg:     Math.floor(Math.random() * 2) + 3,
      gap:     Math.random() * 8 + 6,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // ── zigzag lines ─────────────────────────────────────────────────────────
      zigs.forEach(z => {
        ctx.save();
        ctx.globalAlpha = z.opacity;
        ctx.strokeStyle = T.gold;
        ctx.lineWidth   = 1;
        ctx.lineJoin    = "round";
        ctx.beginPath();

        let cx = z.x, cy = z.y;
        let dir = 1;
        ctx.moveTo(cx, cy);

        for (let i = 0; i < z.seg; i++) {
          const nx = cx + Math.cos(z.angle) * z.gap;
          const ny = cy + Math.sin(z.angle) * z.gap + dir * z.gap * 0.6;
          ctx.lineTo(nx, ny);
          cx = nx; cy = ny;
          dir *= -1;
        }
        ctx.stroke();
        ctx.restore();

        z.x += Math.cos(z.angle) * z.speed;
        z.y += Math.sin(z.angle) * z.speed * 0.4;

        if (z.x > W + 60) z.x = -60;
        if (z.x < -60)    z.x = W + 60;
        if (z.y > H + 60) z.y = -60;
        if (z.y < -60)    z.y = H + 60;
      });

      // ── connection lines between close particles ────────────────────────────
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 90) {
            ctx.save();
            ctx.globalAlpha = (1 - dist / 90) * 0.08;
            ctx.strokeStyle = T.gold;
            ctx.lineWidth   = 0.6;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      // ── particles ─────────────────────────────────────────────────────────────
      particles.forEach(p => {
        ctx.save();
        ctx.globalAlpha = p.opacity;

        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 2.2);
        grad.addColorStop(0, T.gold);
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 2.2, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = T.gold;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.7 }}
    />
  );
}

// ─── Floral corners ────────────────────────────────────────────────────────────
function FloralCorners() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none select-none" viewBox="0 0 800 900" preserveAspectRatio="xMidYMid slice">
      {[[80,80],[40,115],[130,50],[20,50],[140,125]].map(([cx,cy],i)=>(
        <g key={`tl${i}`}>
          {[0,60,120,180,240,300].map(a=>{
            const r=i===0?26:18;
            const px=cx+r*Math.cos(a*Math.PI/180);
            const py=cy+r*Math.sin(a*Math.PI/180);
            return <circle key={a} cx={px} cy={py} r={i===0?7:5} fill={T.champagne} opacity="0.6"/>;
          })}
          <circle cx={cx} cy={cy} r={i===0?8:6} fill={T.gold} opacity="0.3"/>
        </g>
      ))}
      <line x1="80" y1="140" x2="40" y2="108" stroke={T.gold} strokeWidth="1.5" opacity="0.35"/>
      <line x1="80" y1="115" x2="130" y2="58" stroke={T.gold} strokeWidth="1" opacity="0.28"/>
      <line x1="80" y1="95"  x2="20"  y2="58" stroke={T.gold} strokeWidth="1" opacity="0.22"/>
      <line x1="80" y1="80"  x2="80"  y2="155" stroke={T.gold} strokeWidth="1.5" opacity="0.32"/>

      {[[720,820],[760,780],[680,850],[770,850],[680,760]].map(([cx,cy],i)=>(
        <g key={`br${i}`}>
          {[0,60,120,180,240,300].map(a=>{
            const r=i===0?26:17;
            const px=cx+r*Math.cos(a*Math.PI/180);
            const py=cy+r*Math.sin(a*Math.PI/180);
            return <circle key={a} cx={px} cy={py} r={i===0?7:5} fill={T.champagne} opacity="0.55"/>;
          })}
          <circle cx={cx} cy={cy} r={i===0?8:6} fill={T.gold} opacity="0.27"/>
        </g>
      ))}
      <line x1="720" y1="820" x2="760" y2="778" stroke={T.gold} strokeWidth="1.5" opacity="0.32"/>
      <line x1="720" y1="830" x2="678" y2="858" stroke={T.gold} strokeWidth="1"   opacity="0.24"/>
      <line x1="720" y1="820" x2="720" y2="745" stroke={T.gold} strokeWidth="1.5" opacity="0.3"/>

      {[[740,55],[778,88],[702,38]].map(([cx,cy],i)=>(
        <g key={`tr${i}`}>
          {[0,72,144,216,288].map(a=>{
            const px=cx+13*Math.cos(a*Math.PI/180);
            const py=cy+13*Math.sin(a*Math.PI/180);
            return <circle key={a} cx={px} cy={py} r="4" fill={T.champagne} opacity="0.45"/>;
          })}
          <circle cx={cx} cy={cy} r="5" fill={T.gold} opacity="0.22"/>
        </g>
      ))}

      {[[58,842],[28,800],[98,862]].map(([cx,cy],i)=>(
        <g key={`bl${i}`}>
          {[0,72,144,216,288].map(a=>{
            const px=cx+13*Math.cos(a*Math.PI/180);
            const py=cy+13*Math.sin(a*Math.PI/180);
            return <circle key={a} cx={px} cy={py} r="4" fill={T.champagne} opacity="0.42"/>;
          })}
          <circle cx={cx} cy={cy} r="5" fill={T.gold} opacity="0.2"/>
        </g>
      ))}
    </svg>
  );
}

export default function AdminLogin({ onLogin }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim())    return setError("Email required");
    if (!password.trim()) return setError("Password required");
    setLoading(true);
    try {
      const res = await apiFetch("/auth/login.php", {
        method: "POST",
        auth: false,
        body: { email: email.trim(), password },
      });
      setToken(res.data.token);
      onLogin && onLogin(res.data.admin);
    } catch (err) {
      setError(err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: T.bg, fontFamily: "'DM Sans','Inter',sans-serif" }}>

      {/* Particle + zigzag canvas */}
      <ParticleCanvas />

      {/* Floral corners */}
      <FloralCorners />

      {/* Card — tightened width + padding for a more compact, premium feel */}
      <div className="relative z-10 w-full max-w-sm mx-4">
        <div className="rounded-2xl overflow-hidden"
          style={{ background: "rgba(255,255,255,0.94)", backdropFilter: "blur(16px)", boxShadow: "0 20px 50px rgba(200,164,106,0.16), 0 4px 18px rgba(0,0,0,0.08)", border: `1px solid ${T.border}` }}>

          {/* Gold stripe */}
          <div className="h-1" style={{ background: `linear-gradient(90deg,${T.champagne},${T.gold},${T.champagne})` }}/>

          <div className="px-8 py-7">
            {/* Logo */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3"
                style={{ background: `linear-gradient(135deg,${T.gold},${T.goldD})`, boxShadow: `0 6px 18px rgba(200,164,106,0.38)` }}>
                <span className="text-white text-lg font-bold" style={{ fontFamily: "'Cormorant Garamond',Georgia,serif" }}>A</span>
              </div>
              <h1 className="text-[24px] font-bold leading-tight" style={{ color: T.textPri, fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
                Aishcraft
              </h1>
              <p className="text-[10px] tracking-[0.2em] uppercase mt-1" style={{ color: T.gold }}>Admin Panel</p>
            </div>

            {/* Welcome */}
            <div className="mb-5 text-center">
              <h2 className="text-[15px] font-bold" style={{ color: T.textPri }}>Welcome back</h2>
              <p className="text-[11.5px] mt-1" style={{ color: T.textSec }}>Sign in to manage your store</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {/* Email */}
              <div>
                <label className="block text-[10.5px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textSec }}>Email</label>
                <div className="relative">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: T.textSec }}>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                    placeholder="admin@aishcraft.com"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl text-[13px] outline-none transition-all"
                    style={{ background: T.bgSoft, border: `1.5px solid ${error?T.danger:T.border}`, color: T.textPri }}
                    onFocus={e => e.target.style.borderColor = T.gold}
                    onBlur={e  => e.target.style.borderColor = error ? T.danger : T.border}/>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-[10.5px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textSec }}>Password</label>
                <div className="relative">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: T.textSec }}>
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input type={showPass?"text":"password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }}
                    placeholder="••••••••••"
                    className="w-full pl-10 pr-11 py-2.5 rounded-xl text-[13px] outline-none transition-all"
                    style={{ background: T.bgSoft, border: `1.5px solid ${error?T.danger:T.border}`, color: T.textPri }}
                    onFocus={e => e.target.style.borderColor = T.gold}
                    onBlur={e  => e.target.style.borderColor = error ? T.danger : T.border}/>
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:opacity-70" style={{ color: T.textSec }}>
                    {showPass
                      ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-[11.5px] font-medium"
                  style={{ background:"#FFF0F0", color:T.danger, border:`1px solid ${T.danger}30` }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 flex-shrink-0">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-[13.5px] font-bold text-white transition-all active:scale-[0.98] mt-1.5 flex items-center justify-center gap-2"
                style={{ background: loading ? T.border : `linear-gradient(135deg,${T.gold},${T.goldD})`, cursor: loading?"not-allowed":"pointer", boxShadow: loading?"none":`0 6px 18px rgba(200,164,106,0.38)` }}>
                {loading ? (
                  <>
                    <style>{`@keyframes lgspin{to{transform:rotate(360deg)}}`}</style>
                    <div style={{ width:16,height:16,border:"2.5px solid rgba(255,255,255,0.35)",borderTopColor:"white",borderRadius:"50%",animation:"lgspin 0.8s linear infinite" }}/>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px" style={{ background: T.border }}/>
              <span className="text-[9.5px] font-semibold tracking-wider uppercase" style={{ color: T.textSec }}>Secure Login</span>
              <div className="flex-1 h-px" style={{ background: T.border }}/>
            </div>

          </div>

          {/* Footer */}
          <div className="px-8 py-3 text-center" style={{ borderTop:`1px solid ${T.border}`, background: T.bgSoft }}>
            <p className="text-[9.5px]" style={{ color: T.textSec }}>© 2025 Aishcraft · Crafting Your Stories</p>
          </div>
        </div>
      </div>
    </div>
  );
}